import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeSwitcherProvider } from 'react-css-theme-switcher';
import store from './store';
import history from './history';
import Layouts from './layouts';
import { THEME_CONFIG } from './configs/AppConfig';
import './lang';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ModalContainer from 'react-modal-promise';
import { arrancar } from 'auth/sesionRedux';

const themes = {
  dark: `${process.env.PUBLIC_URL}/css/dark-theme.css`,
  light: `${process.env.PUBLIC_URL}/css/light-theme.css`,
};

function App() {
  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000,
          refetchOnWindowFocus: true,
        },
      },
    })
  );

  // Arranque de la sesión: authClient.boot() antes de que Layouts pinte nada
  // (mientras tanto auth.booting deja el Loading en pantalla) y cableado de
  // los eventos del cliente al store.
  //
  // La bandera `vivo` es por el doble montaje de React.StrictMode en
  // desarrollo: la limpieza corre antes de que arrancar() resuelva, así que
  // sin esto los oyentes del primer montaje se quedarían registrados.
  useEffect(() => {
    let vivo = true;
    let parar = null;
    arrancar(store).then((p) => {
      if (vivo) parar = p;
      else p();
    });
    return () => {
      vivo = false;
      if (parar) parar();
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      console.log('handleVisibility', document.visibilityState);
      if (document.visibilityState === 'visible') {
        const event = new CustomEvent('restore-ui-after-download');
        window.dispatchEvent(event);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools
          position="left"
          initialIsOpen={false}
          client={queryClient}
        />
        <Provider store={store}>
          <ModalContainer />
          <BrowserRouter history={history}>
            <ThemeSwitcherProvider
              themeMap={themes}
              defaultTheme={THEME_CONFIG.currentTheme}
              insertionPoint="styles-insertion-point"
            >
              <Layouts />
            </ThemeSwitcherProvider>
          </BrowserRouter>
        </Provider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
